import type { HardwareGraph, HardwareNode } from './types';

function widthLabel(width: number) {
  return width > 1 ? `[${width - 1}:0]` : '1 bit';
}

function sourceLabel(node: HardwareNode, fallbackFile: string) {
  if (!node.source) return '—';
  return `${node.source.file || fallbackFile}:${node.source.startLine}-${node.source.endLine}`;
}

function cell(value: unknown) {
  return String(value ?? '—').replaceAll('|', '\\|').replaceAll('\n', ' ');
}

export function buildDesignSummary(graph: HardwareGraph, filename: string) {
  const registers = graph.nodes.filter((node) => node.type === 'register');
  const lines = [
    `# ${cell(graph.module)} — RTL Design Summary`,
    '',
    `- **Source:** \`${cell(filename)}\``,
    `- **Hardware IR:** schema ${graph.schemaVersion}`,
    `- **Topology:** ${graph.ports.length} ports, ${graph.nodes.length} components, ${graph.edges.length} nets`,
    '',
    '## Ports',
    '',
    '| Name | Direction | Width | Signal class | Source |',
    '| --- | --- | ---: | --- | --- |',
    ...graph.ports.map((port) => `| ${cell(port.label)} | ${cell(port.properties.direction ?? port.type)} | ${widthLabel(port.width)} | ${cell(port.properties.signalType ?? 'data')} | ${sourceLabel(port, filename)} |`),
    '',
    '## Registers',
    '',
    '| Register | Width | Clock | Reset | Enable | Source |',
    '| --- | ---: | --- | --- | --- | --- |',
    ...(registers.length ? registers.map((node) => `| ${cell(node.label)} | ${widthLabel(node.width)} | ${cell(node.properties.clock)} ${cell(node.properties.clockEdge)} | ${cell(node.properties.reset)} ${cell(node.properties.resetPolarity)} | ${cell(node.properties.enable)} | ${sourceLabel(node, filename)} |`) : ['| _No registers inferred_ | — | — | — | — | — |']),
    '',
    '## State Machines',
    '',
  ];

  if (!graph.fsms?.length) lines.push('_No finite state machines were recognized._', '');
  graph.fsms?.forEach((fsm) => {
    lines.push(`### ${cell(fsm.name)}`, '', `State signal: \`${cell(fsm.stateSignal)}\`${fsm.resetState ? ` · Reset state: \`${cell(fsm.resetState)}\`` : ''}`, '', '```mermaid', 'stateDiagram-v2');
    fsm.transitions.forEach((transition) => lines.push(`  ${cell(transition.source)} --> ${cell(transition.target)}: ${cell(transition.condition)}`));
    lines.push('```', '', '| State | Encoding | Source |', '| --- | --- | --- |');
    fsm.states.forEach((state) => lines.push(`| ${cell(state.name)} | ${cell(state.value)} | ${state.source ? `${state.source.file}:${state.source.startLine}-${state.source.endLine}` : '—'} |`));
    lines.push('');
  });

  lines.push('## Register Transfer Notes', '', ...registers.map((node) => `- **${cell(node.label)}**: ${cell(node.properties.expression ?? 'registered value')} (${cell(node.properties.resetType ?? 'reset type not reported')})`), '', '> Generated by Ortl directly from Hardware IR. Verify inferred behavior before sign-off.', '');
  return lines.join('\n');
}

export function downloadText(filename: string, contents: string, type = 'text/plain') {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
